import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { Card, CardBody } from '@structural-climbing/ui'

interface Props {
  label: ReactNode
  value: ReactNode
  /** One short line under the number. */
  hint?: ReactNode
  hintTone?: 'muted' | 'good' | 'warning'
  /** Opens the screen behind the number. Null keeps it a plain number. */
  to?: string | null
  /** Extra content under the hint (e.g. income per payment method). */
  children?: ReactNode
}

const HINT_CLASS = {
  muted: 'text-foreground/55',
  good: 'text-success-600',
  warning: 'text-warning-600',
}

/** A number on a white card: label, value, one line of context. */
export function StatTile({ label, value, hint, hintTone = 'muted', to, children }: Props) {
  const navigate = useNavigate()
  const body = (
    <CardBody className="gap-0.5 px-4 py-3.5">
      <p className="text-sm text-foreground/60">{label}</p>
      <p className="text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">{value}</p>
      {hint && <p className={`text-xs ${HINT_CLASS[hintTone]}`}>{hint}</p>}
      {children}
    </CardBody>
  )
  if (!to)
    return (
      <Card shadow="sm" className="h-full">
        {body}
      </Card>
    )
  return (
    <Card shadow="sm" isPressable onPress={() => navigate(to)} className="h-full w-full text-left">
      {body}
    </Card>
  )
}
